"use client";

import * as React from "react";
import { cn, Skeleton, SkeletonRow } from "@aio/platform-ui";

/** Default number of skeleton placeholders rendered while data loads. */
export const DEFAULT_LOADING_ROW_COUNT = 8;

/**
 * Props for {@link LoadingTable}.
 *
 * The loading state has two visual modes:
 *
 * - **Standalone** (default): renders {@link SkeletonRow} from
 *   `@aio/platform-ui` `rowCount` times in a stacked layout. Useful when
 *   the loading state replaces an entire toolbar+table block instead of
 *   slotting inside a `<table>`.
 * - **In-table** (when `columnCount` is provided): renders `rowCount`
 *   `<tr>` rows, each with `columnCount` `<td>` cells holding a single
 *   {@link Skeleton} block. The selection column is included in the
 *   count by the caller — pass the same `columnCount` you'd pass as
 *   `colSpan` to {@link EmptyTable}.
 */
export interface LoadingTableProps {
  /** Number of skeleton rows to render. Defaults to {@link DEFAULT_LOADING_ROW_COUNT}. */
  rowCount?: number;
  /**
   * When set, the loading state renders inside a `<tbody>` as `<tr>` rows
   * with `columnCount` `<td>` cells each. When omitted, the standalone
   * `<div>` mode kicks in.
   */
  columnCount?: number;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /** Test id forwarded to the outer wrapper. */
  "data-testid"?: string;
}

/**
 * Loading-state wrapper for the table engine.
 *
 * The defaults match the plan: 8 `SkeletonRow` placeholders in standalone
 * mode. When the engine knows the visible column count up-front it should
 * pass `columnCount`, which switches the renderer to `<tr>`/`<td>` mode so
 * the table layout (column widths, sticky cells) survives the loading
 * phase without a layout shift when real rows arrive.
 *
 * The role/aria-label combo on the outer wrapper turns the whole panel
 * into a single live region so screen readers announce the loading state
 * once instead of N times (one per skeleton).
 */
export function LoadingTable({
  rowCount = DEFAULT_LOADING_ROW_COUNT,
  columnCount,
  className,
  "data-testid": dataTestId = "loading-table",
}: LoadingTableProps): React.JSX.Element {
  // Clamp the row count so a caller passing 0 or a negative number still
  // gets *something* on screen — otherwise the loading state would render
  // as an empty wrapper and the table would look broken.
  const safeRowCount = Math.max(1, rowCount);

  if (columnCount !== undefined && columnCount > 0) {
    // In-table mode: emit a tbody fragment of <tr> rows. The wrapper has
    // no element of its own — it returns a fragment so the caller can
    // splice it directly into a `<tbody>` without inserting an invalid
    // `<div>` ancestor. The role/aria-label live on the first row's first
    // cell so screen readers still get the announcement.
    return (
      <>
        {Array.from({ length: safeRowCount }).map((_, rowIdx) => (
          <tr
            key={rowIdx}
            data-testid={`${dataTestId}-row-${rowIdx}`}
            className={cn("border-b border-border", className)}
            // Surface the live region on the first row only — duplicate
            // announcements would be noisy.
            {...(rowIdx === 0
              ? {
                  role: "row",
                  "aria-busy": true,
                  "aria-label": "Loading table data",
                }
              : { "aria-hidden": true })}
          >
            {Array.from({ length: columnCount }).map((__, colIdx) => (
              <td
                key={colIdx}
                className="px-3 py-3 align-middle"
                // The skeleton itself owns role=status with aria-label
                // "Loading" via @aio/platform-ui — we suppress those on
                // the inner so the row's announcement is the canonical
                // one.
              >
                <Skeleton className="h-4 w-full" aria-hidden="true" />
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  }

  // Standalone mode: stacked SkeletonRow blocks inside a div wrapper. The
  // wrapper itself owns the live region.
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading table data"
      className={cn("flex flex-col gap-1", className)}
      data-testid={dataTestId}
    >
      {Array.from({ length: safeRowCount }).map((_, idx) => (
        <SkeletonRow key={idx} />
      ))}
    </div>
  );
}
